import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  clientsApi,
  catalogApi,
  CatalogRestrictions,
  MergedCatalog,
  CatalogTheme,
  CatalogAdapter,
  SectionVariant,
} from "../api";
import {
  Settings,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Palette,
  FileText,
  Layers,
  Database,
  ChevronDown,
  ChevronRight,
  Info,
  X,
} from "lucide-react";
import clsx from "clsx";

interface CatalogRestrictionsEditorProps {
  clientId: string;
}

// Helper per normalizzare varianti (possono essere stringhe o oggetti)
function normalizeVariants(
  variants: SectionVariant[] | string[] | undefined,
): SectionVariant[] {
  if (!variants) return [];
  return variants.map((v) => (typeof v === "string" ? { id: v, label: v } : v));
}

export function CatalogRestrictionsEditor({
  clientId,
}: CatalogRestrictionsEditorProps) {
  const queryClient = useQueryClient();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    themes: true,
    templates: false,
    sections: true,
    adapters: false,
  });

  const [restrictions, setRestrictions] = useState<CatalogRestrictions>({
    catalogVersion: 1,
    allowedThemes: [],
    allowedPageKinds: [],
    allowedTemplates: [],
    allowedSections: [],
    allowedAdapters: [],
    customSections: {},
    customAdapters: [],
  });

  const [hasChanges, setHasChanges] = useState(false);

  // Carica catalogo master
  const { data: masterData, isLoading: isLoadingMaster } = useQuery({
    queryKey: ["catalog", "current"],
    queryFn: () => catalogApi.getCurrent(),
  });
  // Struttura: response.data = { success, data: { version, status, catalog: {...} } }
  const masterCatalog = masterData?.data?.data?.catalog;

  // Carica catalogo cliente (merged)
  const { data: clientCatalogData, isLoading: isLoadingClient } = useQuery({
    queryKey: ["client-catalog", clientId],
    queryFn: () => clientsApi.getCatalog(clientId),
  });
  const clientRestrictions = clientCatalogData?.data?.data?.restrictions;
  const mergedCatalog = clientCatalogData?.data?.data?.catalog as
    | MergedCatalog
    | undefined;

  // Inizializza restrictions quando arrivano i dati
  useEffect(() => {
    if (clientRestrictions) {
      setRestrictions({
        catalogVersion: clientRestrictions.catalogVersion || 1,
        allowedThemes: clientRestrictions.allowedThemes || [],
        allowedPageKinds: clientRestrictions.allowedPageKinds || [],
        allowedTemplates: clientRestrictions.allowedTemplates || [],
        allowedSections: clientRestrictions.allowedSections || [],
        allowedAdapters: clientRestrictions.allowedAdapters || [],
        customSections: clientRestrictions.customSections || {},
        customAdapters: clientRestrictions.customAdapters || [],
      });
    }
  }, [clientRestrictions]);

  // Mutation per salvare
  const saveMutation = useMutation({
    mutationFn: () => clientsApi.updateCatalog(clientId, restrictions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-catalog", clientId] });
      queryClient.invalidateQueries({ queryKey: ["client", clientId] });
      setHasChanges(false);
    },
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleItem = (
    field:
      | "allowedThemes"
      | "allowedTemplates"
      | "allowedSections"
      | "allowedAdapters",
    item: string,
  ) => {
    setRestrictions((prev) => {
      const current = prev[field] || [];
      const updated = current.includes(item)
        ? current.filter((i) => i !== item)
        : [...current, item];
      return { ...prev, [field]: updated };
    });
    setHasChanges(true);
  };

  const selectAll = (
    field:
      | "allowedThemes"
      | "allowedTemplates"
      | "allowedSections"
      | "allowedAdapters",
    items: string[],
  ) => {
    setRestrictions((prev) => ({ ...prev, [field]: items }));
    setHasChanges(true);
  };

  const selectNone = (
    field:
      | "allowedThemes"
      | "allowedTemplates"
      | "allowedSections"
      | "allowedAdapters",
  ) => {
    setRestrictions((prev) => ({ ...prev, [field]: [] }));
    setHasChanges(true);
  };

  const isLoading = isLoadingMaster || isLoadingClient;

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Caricamento catalogo...
      </div>
    );
  }

  if (!masterCatalog) {
    return (
      <div className="p-8 text-center text-red-600">
        <AlertCircle className="w-6 h-6 mx-auto mb-2" />
        Catalogo master non trovato
      </div>
    );
  }

  // Normalizza temi e adapters (supporta entrambi i formati)
  const themes: CatalogTheme[] = Array.isArray(masterCatalog.themes)
    ? masterCatalog.themes
    : Object.entries(masterCatalog.themes || {}).map(
        ([id, v]: [string, any]) => ({
          id,
          label: v.label || id,
          description: v.description,
          preview: v.preview || v.colors,
        }),
      );

  const templates = masterCatalog.templates || {};
  const sections = masterCatalog.sections || {};

  const adapters: CatalogAdapter[] = Array.isArray(masterCatalog.adapters)
    ? typeof masterCatalog.adapters[0] === "string"
      ? (masterCatalog.adapters as unknown as string[]).map((id) => ({
          id,
          label: id,
          description: "",
        }))
      : (masterCatalog.adapters as CatalogAdapter[])
    : [{ id: "demo", label: "Demo", description: "Mock data" }];

  const isNoneSelected = (field: string[]) => field.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Settings className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Restrizioni Catalogo
            </h3>
            <p className="text-sm text-gray-500">
              Seleziona cosa è disponibile per questo cliente
            </p>
          </div>
        </div>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={!hasChanges || saveMutation.isPending}
          className={clsx(
            "btn-primary",
            !hasChanges && "opacity-50 cursor-not-allowed",
          )}
        >
          {saveMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Salva restrizioni
        </button>
      </div>

      {/* Info box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Come funziona</p>
          <p className="mt-1">
            Se non selezioni nulla, il cliente eredita <strong>tutto</strong>{" "}
            dal catalogo master. Se selezioni almeno un elemento, il cliente
            avrà accesso <strong>solo</strong> a quelli selezionati.
          </p>
        </div>
      </div>

      {/* Success message */}
      {saveMutation.isSuccess && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2 text-green-700">
          <CheckCircle className="w-5 h-5" />
          <span>Restrizioni salvate con successo</span>
        </div>
      )}

      {/* Themes */}
      <div className="card">
        <button
          onClick={() => toggleSection("themes")}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center space-x-3">
            <Palette className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-900">Temi</span>
            <span className="text-sm text-gray-500">
              ({restrictions.allowedThemes?.length || 0} / {themes.length})
            </span>
          </div>
          {expandedSections.themes ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.themes && (
          <div className="px-4 pb-4 border-t">
            <div className="flex items-center space-x-4 py-2 text-sm">
              <button
                onClick={() =>
                  selectAll(
                    "allowedThemes",
                    themes.map((t) => t.id),
                  )
                }
                className="text-primary-600 hover:underline"
              >
                Seleziona tutti
              </button>
              <button
                onClick={() => selectNone("allowedThemes")}
                className="text-gray-500 hover:underline"
              >
                Deseleziona tutti
              </button>
              {isNoneSelected(restrictions.allowedThemes || []) && (
                <span className="text-amber-600 text-xs">
                  (eredita tutto dal master)
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {themes.map((theme) => (
                <label
                  key={theme.id}
                  className={clsx(
                    "flex items-center p-3 rounded-lg border cursor-pointer transition-colors",
                    restrictions.allowedThemes?.includes(theme.id)
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={
                      restrictions.allowedThemes?.includes(theme.id) || false
                    }
                    onChange={() => toggleItem("allowedThemes", theme.id)}
                    className="sr-only"
                  />
                  <div
                    className="w-4 h-4 rounded mr-3 border"
                    style={{
                      backgroundColor: theme.preview?.primary || "#888",
                    }}
                  />
                  <div>
                    <p className="font-medium text-sm">{theme.label}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Templates */}
      <div className="card">
        <button
          onClick={() => toggleSection("templates")}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center space-x-3">
            <FileText className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-900">Templates</span>
            <span className="text-sm text-gray-500">
              ({restrictions.allowedTemplates?.length || 0} /{" "}
              {Object.keys(templates).length})
            </span>
          </div>
          {expandedSections.templates ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.templates && (
          <div className="px-4 pb-4 border-t">
            <div className="flex items-center space-x-4 py-2 text-sm">
              <button
                onClick={() =>
                  selectAll("allowedTemplates", Object.keys(templates))
                }
                className="text-primary-600 hover:underline"
              >
                Seleziona tutti
              </button>
              <button
                onClick={() => selectNone("allowedTemplates")}
                className="text-gray-500 hover:underline"
              >
                Deseleziona tutti
              </button>
            </div>
            <div className="space-y-2">
              {Object.entries(templates).map(([key, template]) => (
                <label
                  key={key}
                  className={clsx(
                    "flex items-center p-3 rounded-lg border cursor-pointer transition-colors",
                    restrictions.allowedTemplates?.includes(key)
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={
                      restrictions.allowedTemplates?.includes(key) || false
                    }
                    onChange={() => toggleItem("allowedTemplates", key)}
                    className="mr-3"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {template.label || key}
                    </p>
                    <p className="text-xs text-gray-500">
                      Slots: {template.slots?.join(", ") || "nessuno"}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="card">
        <button
          onClick={() => toggleSection("sections")}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center space-x-3">
            <Layers className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-900">Sezioni</span>
            <span className="text-sm text-gray-500">
              ({restrictions.allowedSections?.length || 0} /{" "}
              {Object.keys(sections).length})
            </span>
          </div>
          {expandedSections.sections ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.sections && (
          <div className="px-4 pb-4 border-t">
            <div className="flex items-center space-x-4 py-2 text-sm">
              <button
                onClick={() =>
                  selectAll("allowedSections", Object.keys(sections))
                }
                className="text-primary-600 hover:underline"
              >
                Seleziona tutti
              </button>
              <button
                onClick={() => selectNone("allowedSections")}
                className="text-gray-500 hover:underline"
              >
                Deseleziona tutti
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(sections).map(([key, section]) => {
                const variants = normalizeVariants(section.variants);
                const plannedCount = variants.filter(
                  (v) => v.status === "planned",
                ).length;

                return (
                  <label
                    key={key}
                    className={clsx(
                      "flex items-center p-3 rounded-lg border cursor-pointer transition-colors",
                      restrictions.allowedSections?.includes(key)
                        ? "border-primary-500 bg-primary-50"
                        : "border-gray-200 hover:border-gray-300",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={
                        restrictions.allowedSections?.includes(key) || false
                      }
                      onChange={() => toggleItem("allowedSections", key)}
                      className="mr-3"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">
                        {section.label || key}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        Slots: {section.allowedSlots?.join(", ")} • Varianti:{" "}
                        {variants.map((v) => v.label || v.id).join(", ")}
                        {plannedCount > 0 && (
                          <span className="ml-1 text-amber-600">
                            ({plannedCount} planned)
                          </span>
                        )}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Adapters */}
      <div className="card">
        <button
          onClick={() => toggleSection("adapters")}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center space-x-3">
            <Database className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-900">
              Data Source Adapters
            </span>
            <span className="text-sm text-gray-500">
              ({restrictions.allowedAdapters?.length || 0} / {adapters.length})
            </span>
          </div>
          {expandedSections.adapters ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.adapters && (
          <div className="px-4 pb-4 border-t">
            <div className="flex items-center space-x-4 py-2 text-sm">
              <button
                onClick={() =>
                  selectAll(
                    "allowedAdapters",
                    adapters.map((a) => a.id),
                  )
                }
                className="text-primary-600 hover:underline"
              >
                Seleziona tutti
              </button>
              <button
                onClick={() => selectNone("allowedAdapters")}
                className="text-gray-500 hover:underline"
              >
                Deseleziona tutti
              </button>
            </div>
            <div className="space-y-2">
              {adapters.map((adapter) => (
                <label
                  key={adapter.id}
                  className={clsx(
                    "flex items-center p-3 rounded-lg border cursor-pointer transition-colors",
                    restrictions.allowedAdapters?.includes(adapter.id)
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={
                      restrictions.allowedAdapters?.includes(adapter.id) ||
                      false
                    }
                    onChange={() => toggleItem("allowedAdapters", adapter.id)}
                    className="mr-3"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm">{adapter.label}</p>
                    {adapter.description && (
                      <p className="text-xs text-gray-500">
                        {adapter.description}
                      </p>
                    )}
                    {adapter.capabilities && (
                      <p className="text-xs text-gray-400">
                        Capabilities: {adapter.capabilities.join(", ")}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {/* Custom adapters */}
            {restrictions.customAdapters &&
              restrictions.customAdapters.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Adapters custom
                  </p>
                  <div className="space-y-2">
                    {restrictions.customAdapters.map((adapter, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <span className="font-mono text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded">
                          {adapter}
                        </span>
                        <button
                          onClick={() => {
                            setRestrictions((prev) => ({
                              ...prev,
                              customAdapters:
                                prev.customAdapters?.filter(
                                  (_, j) => j !== i,
                                ) || [],
                            }));
                            setHasChanges(true);
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        )}
      </div>

      {/* Summary */}
      {mergedCatalog && (
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-2">
            Riepilogo catalogo effettivo
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Temi:</span>{" "}
              <span className="font-medium">
                {mergedCatalog.themes?.length || 0}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Templates:</span>{" "}
              <span className="font-medium">
                {Object.keys(mergedCatalog.templates || {}).length}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Sezioni:</span>{" "}
              <span className="font-medium">
                {Object.keys(mergedCatalog.sections || {}).length}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Adapters:</span>{" "}
              <span className="font-medium">
                {mergedCatalog.adapters?.length || 0}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
